// Cliente de la API pública. Con VITE_API_URL habla HTTP con la API real; sin ella usa datos de demostración
// (mismo contrato, tipado desde @cuorum/contracts), para que la página se desarrolle antes que la API.
import type {
  PublicApiErrorCode, PublicApiErrorDto, PublicEventDto, RegistrationRequestDto, RegistrationResponseDto,
} from '@cuorum/contracts/public-events';
import { DEMO_EVENTS, publishedDemoEvents } from './demoEvents.ts';

export class PublicApiError extends Error {
  constructor(readonly code: PublicApiErrorCode, message: string) {
    super(message);
    this.name = 'PublicApiError';
  }
}

export interface PublicApi {
  readonly mode: 'http' | 'demo';
  getEvent(accountSlug: string, eventSlug: string, signal?: AbortSignal): Promise<PublicEventDto>;
  register(accountSlug: string, eventSlug: string, body: RegistrationRequestDto): Promise<RegistrationResponseDto>;
  listDemoEvents?(): PublicEventDto[];
}

export function createApi(baseUrl: string | undefined): PublicApi {
  return baseUrl ? createHttpApi(baseUrl) : createDemoApi();
}

function createHttpApi(baseUrl: string): PublicApi {
  const root = baseUrl.replace(/\/$/, '');
  const path = (a: string, e: string) => `${root}/public/v1/accounts/${encodeURIComponent(a)}/events/${encodeURIComponent(e)}`;

  async function parse<T>(res: Response): Promise<T> {
    if (res.ok) return (await res.json()) as T;
    let body: Partial<PublicApiErrorDto> = {};
    try { body = (await res.json()) as PublicApiErrorDto; } catch { /* cuerpo no JSON: se usa el estado */ }
    const code: PublicApiErrorCode = body.error ?? (res.status === 404 ? 'not_found' : res.status === 429 ? 'rate_limited' : 'unavailable');
    throw new PublicApiError(code, body.message ?? `HTTP ${res.status}`);
  }

  return {
    mode: 'http',
    async getEvent(a, e, signal) {
      return parse<PublicEventDto>(await fetch(path(a, e), { signal, headers: { accept: 'application/json' } }));
    },
    async register(a, e, body) {
      return parse<RegistrationResponseDto>(await fetch(`${path(a, e)}/registrations`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'idempotency-key': body.idempotencyKey },
        body: JSON.stringify(body),
      }));
    },
  };
}

function createDemoApi(): PublicApi {
  const find = (a: string, e: string) => [...publishedDemoEvents(), ...DEMO_EVENTS].find((x) => x.account.slug === a && x.event.slug === e);
  const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));
  return {
    mode: 'demo',
    async getEvent(a, e) {
      await wait(250);
      const found = find(a, e);
      if (!found) throw new PublicApiError('not_found', 'evento no encontrado');
      return structuredClone(found);
    },
    async register(a, e, body) {
      await wait(500);
      const found = find(a, e);
      if (!found) throw new PublicApiError('not_found', 'evento no encontrado');
      const st = found.event.registration.state;
      if (st !== 'open' && st !== 'full') throw new PublicApiError('registration_closed', 'la inscripción está cerrada');
      if (!body.consent || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(body.email)) throw new PublicApiError('invalid_input', 'datos inválidos');
      return { status: 'received', list: st === 'full' ? 'waitlist' : 'registered' };
    },
    listDemoEvents: () => [...publishedDemoEvents(), ...DEMO_EVENTS],
  };
}
